import React, {FC, useCallback, useRef} from 'react';
import {IProduct, IProductOption} from '../../../../../../../types/galleryTypes';
import {useGlobals, withGlobals} from '../../../../../../../globalPropsContext';
import {useOptionSelectionLogic} from '../../../../hooks/options/useOptionSelectionLogic';
import {BoxSelection, BoxSelectionProps, BoxSelectionBoxSize, BoxSelectionSelectionType} from 'wix-ui-tpa/cssVars';
import {ProductOptionType} from '@wix/wixstores-graphql-schema/dist/es/src/graphql-schema';
import classNames from 'classnames';
import {classes as stylableBoxSelectionClasses} from './ProductQuickAddBoxSelection.st.css';
import s from './ProductQuickAddBoxSelection.scss';
import {useRadioButtonKeyboardNavigation} from '../../hooks/useRadioButtonKeyboardNavigation';

export enum ProductQuickAddBoxSelectionDataHook {
  Root = 'ProductQuickAddBoxSelectionDataHook.Root',
  BoxSelectionElement = 'ProductQuickAddBoxSelectionDataHook.BoxSelectionElement',
  Option = 'ProductQuickAddBoxSelectionDataHook.Option',
}

export interface IProductQuickAddBoxSelectionProps {
  productId: IProduct['id'];
  option: IProductOption;
}

const ProductQuickAddBoxSelectionComp: FC<IProductQuickAddBoxSelectionProps> = ({productId, option}) => {
  const {productsVariantInfoMap, handleProductsOptionsChange, styles, stylesParams} = useGlobals();
  const selectionIds = productsVariantInfoMap?.[productId]?.variantSelectionIds;
  const selectionsAvailability = productsVariantInfoMap?.[productId]?.selectionsAvailability[option.id];

  const {visibleSelections, calculateNextSelectionIds, isSelectionDisabled, isSelectionChecked} =
    useOptionSelectionLogic({
      option,
      selectionIds,
      selectionsAvailability,
    });

  const containerRef = useRef<HTMLDivElement>(null);

  const handleSelection = useCallback(
    (pickedSelectionId: number) => {
      void handleProductsOptionsChange({
        productId,
        selectionIds: calculateNextSelectionIds(pickedSelectionId),
        optionType: ProductOptionType.DROP_DOWN,
      });
    },
    [handleProductsOptionsChange, productId, calculateNextSelectionIds]
  );

  const {handleKeyboardSelection} = useRadioButtonKeyboardNavigation({
    containerRef,
    visibleSelections,
    isSelectionDisabled,
    handleSelection,
  });

  const handleMouseSelection: BoxSelectionProps['onChange'] = useCallback(
    (event) => {
      const pickedSelectionId = Number(event.id.split('-').pop());
      handleSelection(pickedSelectionId);
    },
    [handleSelection]
  );

  const showLabel = styles.get(stylesParams.gallery_quickAddShowProductOptionsTitles);

  return (
    <div
      data-hook={ProductQuickAddBoxSelectionDataHook.Root}
      ref={containerRef}
      onKeyDown={handleKeyboardSelection}
      role="none">
      <BoxSelection
        data-hook={ProductQuickAddBoxSelectionDataHook.BoxSelectionElement}
        className={classNames(stylableBoxSelectionClasses.root, s.boxSelection)}
        label={showLabel ? option.title : undefined}
        aria-label={option.title}
        required
        showAsterisk
        size={BoxSelectionBoxSize.xSmall}
        name={`button-option-${option.id}`}
        onChange={handleMouseSelection}>
        {visibleSelections.map((selection, index) => {
          const isDisabled = isSelectionDisabled(selection.id);
          const isSelected = !isDisabled && isSelectionChecked(selection.id);

          return (
            <BoxSelection.Option
              data-hook={ProductQuickAddBoxSelectionDataHook.Option}
              className={s.boxSelectionOption}
              type={BoxSelectionSelectionType.RADIO_BUTTON}
              key={selection.id}
              id={`${option.id}-${selection.id}`}
              aria-label={selection.value}
              checked={isSelected}
              disabled={isDisabled}>
              <div
                data-hook={`box-selection-text-option-${index}`}
                className={classNames(s.boxSelectionOptionText, {
                  [s.disabled]: isDisabled,
                  [s.selected]: isSelected,
                })}>
                {selection.value}
              </div>
            </BoxSelection.Option>
          );
        })}
      </BoxSelection>
    </div>
  );
};

export const ProductQuickAddBoxSelection = withGlobals(ProductQuickAddBoxSelectionComp);
